// Dashboard: "overview dan visualisasi data, serta summary dari data yang ada".
// One generic page for every tenant, driven only by GET /api/i/:slug/overview.
//
// Direction (docs/design/direction.md): a departures board read row by row.
// Section concepts (JEV ui.region_gate, 2026-10-12):
//   Angka utama  job: the two to four headline numbers and where each stands.  one row of KPI cards
//   North Star   job: the one result, its parts, how it moved monthly.          one row: trend + parts
//   Tahap        job: per stage, status and figures first, then its charts.    a band of level rows
//   Rincian      job: formulas, grading rules, document wording, full lists.  one closed disclosure
//
// Every row's spans sum to twelve and every card fills the row's height
// with its visual. No chart kind is drawn more than twice on this page;
// a visual past the cap is another view behind a card's view switch
// (dashboard/plan.ts).
import { useState } from "react";
import {
  Disclosure,
  EmptyState,
  ListRows,
  MetricTiles,
  Notice,
  PageHeader,
  TimeChart,
  VerdictBadge,
  ViewSwitch,
  VizStrip,
  XYScatter,
  VizBand,
  VizCard,
  VizFigure,
  VizRow,
  type ListRowItem,
  type VizSpan,
} from "@dashboard-intelligent/ui";
import type { DashboardUses, MetricValue, Overview, Verdict } from "@dashboard-intelligent/api/src/modules/industry/types";
import { endpoints } from "../endpoints";
import { VERDICT_TEXT, formatMetric, metricFormatter } from "../format";
import { useIndustry } from "../industry";
import { useApi } from "../useApi";
import { bandRows, headlineOf, headlineSpan, nsParts, planDashboard, type BandPlan, type ChartPlan, type DashboardPlan, type OpeningItem } from "./dashboard/plan";
import { MetricKpi, MetricStatus, metricRow, metricTile } from "./shared/figures";
import { figureParts, formatPeriod, isEmptyOverview, isGraded, stageMetrics, trendLabel } from "./shared/metric";
import { VisualChart, displayVisual, visualContext, visualFigure } from "./shared/visual";

export function DashboardPage() {
  const { slug, summary } = useIndustry();
  const state = useApi(() => endpoints.overview(slug));
  const name = summary?.shortName ?? slug;

  if (state.status === "loading") {
    return <PageHeader title="Dashboard" description={`Memuat ringkasan ${name}.`} />;
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Dashboard" description={`Ringkasan ${name}.`} />
        <Notice
          tone="danger"
          title="Ringkasan gagal dimuat"
          action={
            <button type="button" className="btn-secondary" onClick={state.reload}>
              Coba lagi
            </button>
          }
        >
          <p>{state.error}</p>
        </Notice>
      </>
    );
  }

  return <DashboardView overview={state.data} />;
}

export function DashboardView({ overview }: { overview: Overview }) {
  const { industry } = overview;
  const header = <PageHeader title="Dashboard" description={`${industry.name}. Periode ${formatPeriod(industry.periodStart, industry.periodEnd)}.`} />;

  if (isEmptyOverview(overview)) {
    return (
      <>
        {header}
        <EmptyState icon="dashboard" title={`Metrik ${industry.shortName} belum didefinisikan`}>
          {`${northStarLine(overview)}Tahap, metrik, dan visual industri ini sedang disusun dari Metrics Map. Halaman ini terisi sendiri begitu definisinya tersedia.`}
        </EmptyState>
      </>
    );
  }

  const plan = planDashboard(overview);
  const headline = headlineOf(overview);
  const span = headlineSpan(headline.length) as VizSpan;
  return (
    <>
      {header}
      {plan.opening.map((band, i) =>
        band.type === "kpi" ? (
          <VizRow key={`o${i}`} label={`Angka utama ${industry.shortName}`}>
            {headline.map((metric) => (
              <MetricKpi key={metric.id} metric={metric} span={span} />
            ))}
          </VizRow>
        ) : (
          <VizRow key={`o${i}`}>
            {band.items.map((item) => (
              <OpeningCard key={item.type} span={item.span as VizSpan} item={item} overview={overview} plan={plan} />
            ))}
          </VizRow>
        ),
      )}
      {plan.bands
        .filter((band) => band.charts.length > 0)
        .map((band) => (
          <Band key={band.id} band={band} />
        ))}
      <Details overview={overview} plan={plan} />
    </>
  );
}

/** The North Star in one sentence for an empty tenant: the doc's formula, else the summary's sentence. */
function northStarLine(overview: Overview): string {
  const text = overview.northStar.formula.trim() || overview.industry.northStar.trim();
  if (!text) return "";
  return `North Star menurut dokumen: ${text}${/[.!?]$/.test(text) ? "" : "."} `;
}

/* ---------------------------------------------------------------------
 * The opening cards: the North Star, its parts, and the pattern's hero.
 * ------------------------------------------------------------------- */

function OpeningCard({ span, item, overview, plan }: { span: VizSpan; item: OpeningItem; overview: Overview; plan: DashboardPlan }) {
  if (item.type === "hero" && plan.hero) return <ChartCard chart={plan.hero} span={span} bandStage={plan.hero.views[0]!.visual.stageId} />;
  if (item.type === "companion" && plan.companion) return <ChartCard chart={plan.companion} span={span} bandStage={plan.companion.views[0]!.visual.stageId} />;
  if (item.type === "parts") return <PartsCard overview={overview} span={span} />;
  return <NorthStarCard overview={overview} span={span} />;
}

/** The North Star: its figure, then its monthly trend, or its parts when there is no trend. */
function NorthStarCard({ overview, span }: { overview: Overview; span: VizSpan }) {
  const northStar = overview.northStar;
  const { metric } = northStar;
  const title = northStar.label ? `North Star: ${northStar.label}` : "North Star";
  const trend = metric !== null && metric.trend.length >= 2 ? metric : null;
  const value = metric && metric.value !== null ? formatMetric(metric.value, metric.format) : null;
  const parts = nsParts(overview);
  const figure = value ? <VizFigure value={value} label={trend ? aggregateLabel(trend) : "nilai periode ini"} /> : <p className="viz-muted">{metric?.note ?? "Data belum bisa menghitung North Star ini."}</p>;
  return (
    <VizCard
      span={span}
      title={title}
      description={trend ? visualSpan(trend) : parts.length > 0 ? `${parts.length} komponen dari rumusnya` : undefined}
      figure={figure}
      cardId="north-star"
      headingLevel={2}
    >
      {trend ? <NorthStarTrend metric={trend} /> : parts.length > 0 ? <MetricTiles label="Komponen North Star" tiles={parts.map(metricTile)} size={span <= 4 ? "sm" : "md"} /> : null}
    </VizCard>
  );
}

function PartsCard({ overview, span }: { overview: Overview; span: VizSpan }) {
  const parts = nsParts(overview);
  return (
    <VizCard span={span} title="Komponen North Star" description={`${parts.length} metrik dari rumusnya`} cardId="north-star-parts">
      <MetricTiles label="Komponen North Star" tiles={parts.map(metricTile)} size="md" />
    </VizCard>
  );
}

/** How the North Star's one number relates to its monthly points: their average, their total, or the period's own figure. */
function aggregateLabel(metric: MetricValue): string {
  const pts = metric.trend.map((p) => p.value);
  const sum = pts.reduce((a, b) => a + b, 0);
  const mean = sum / pts.length;
  const v = metric.value ?? 0;
  const near = (a: number, b: number) => Math.abs(a - b) <= Math.max(1e-9, Math.abs(b) * 0.02);
  if (near(v, mean)) return "rata-rata per bulan, seluruh periode";
  if (near(v, sum)) return "total seluruh periode";
  return "seluruh periode, dihitung dari semua data";
}

function visualSpan(metric: MetricValue): string {
  const first = metric.trend[0];
  const last = metric.trend[metric.trend.length - 1];
  return first && last ? `${first.label} sampai ${last.label}` : "";
}

function NorthStarTrend({ metric }: { metric: MetricValue }) {
  return (
    <TimeChart
      label={`Tren ${metric.label} per periode`}
      data={metric.trend.map((p) => ({ label: p.label, value: p.value }))}
      series={[{ key: "value", label: metric.label }]}
      format={metricFormatter(metric.format)}
      target={metric.target ? { value: metric.target.target, label: "Target" } : undefined}
    />
  );
}

/* ---------------------------------------------------------------------
 * A stage band: its status, its figures, then its charts.
 * ------------------------------------------------------------------- */

const VERDICTS: Verdict[] = ["attention", "watch", "good"];

function Band({ band }: { band: BandPlan }) {
  const title = band.stage?.label ?? "Lintas tahap";
  const graded = band.metrics.filter(isGraded);
  const status = VERDICTS.map((v) => ({ v, n: graded.filter((m) => m.target.verdict === v).length }))
    .filter((s) => s.n > 0)
    .map((s) => <VerdictBadge key={s.v} verdict={s.v} label={`${s.n} ${VERDICT_TEXT[s.v].toLowerCase()}`} />);
  // One or two figures need no strip of their own: they lead the stage's first chart card.
  const lead = band.metrics.filter((m) => m.value !== null);
  const fold = lead.length > 0 && lead.length <= 2;
  const rows = bandRows(fold ? { ...band, metrics: [] } : band);
  const first = band.charts[0]?.id;
  return (
    <VizBand title={title} meta={band.stage ? `Owner: ${band.stage.owner}` : "Grafik tanpa tahap"} status={status.length > 0 ? status : undefined} id={`tahap-${band.id}`}>
      {rows.map((row, r) => (
        <VizRow key={r}>
          {row.map((item) =>
            item.type === "tiles" ? (
              <StageTiles key="tiles" band={band} span={item.span as VizSpan} />
            ) : (
              <ChartCard key={item.chart.id} chart={item.chart} span={item.span as VizSpan} bandStage={band.stage?.id ?? null} lead={fold && item.chart.id === first ? lead : undefined} />
            ),
          )}
        </VizRow>
      ))}
    </VizBand>
  );
}

function StageTiles({ band, span }: { band: BandPlan; span: VizSpan }) {
  const shown = band.metrics.filter((m) => m.value !== null);
  const missing = band.metrics.filter((m) => m.value === null);
  const label = band.stage?.label ?? "Lintas tahap";
  return (
    <VizStrip
      span={span}
      label={`Angka tahap ${label}`}
      tiles={shown.map(metricTile)}
      footer={missing.length > 0 ? <p>Belum terhitung: {missing.map((m) => m.label).join(", ")}.</p> : undefined}
    />
  );
}

/** One chart card; a card holding more than one view carries a view switch. */
/** What a view is about, short: "Per area" when the views split by different dimensions, else its own title. */
function viewNames(views: ChartPlan["views"]): string[] {
  const name = (v: ChartPlan["views"][number]) =>
    v.visual.kind === "trend"
      ? v.visual.series.length > 1
        ? `${v.visual.series[0]!.label} dan ${v.visual.series.length - 1} lainnya`
        : v.visual.series[0]!.label
      : v.visual.kind === "funnel"
        ? v.visual.valueLabel
        : `${v.visual.valueLabel} per ${v.visual.categoryLabel.toLowerCase()}`;
  const dims = views.map((v) => ("categoryLabel" in v.visual && v.visual.kind !== "trend" ? `Per ${v.visual.categoryLabel.toLowerCase()}` : name(v)));
  if (new Set(dims).size === dims.length) return dims;
  const names = views.map(name);
  return new Set(names).size === names.length ? names : names.map((n, i) => `${n} (${i + 1})`);
}

function ChartCard({ chart, span, bandStage, lead }: { chart: ChartPlan; span: VizSpan; bandStage: string | null; lead?: MetricValue[] }) {
  const [active, setActive] = useState(0);
  if (chart.pair) return <PairCard chart={chart} span={span} lead={lead} />;
  const view = chart.views[active] ?? chart.views[0]!;
  const { visual: raw, mark } = view;
  const visual = displayVisual(raw);
  const foreign = visual.stageId !== null && visual.stageId !== bandStage;
  const names = viewNames(chart.views);
  const controls =
    chart.views.length > 1 ? (
      <ViewSwitch label={`Pilih grafik, ${chart.views.length} grafik di kartu ini`} options={chart.views.map((_, i) => ({ value: String(i), label: names[i]! }))} value={String(active)} onChange={(v) => setActive(Number(v))} />
    ) : undefined;
  return (
    <VizCard
      span={span}
      title={visual.title}
      description={foreign ? `${visualContext(visual)}. Dari tahap lain.` : visualContext(visual)}
      controls={controls}
      figure={lead ? <LeadFigures metrics={lead} /> : visualFigure(raw, mark)}
      cardId={chart.id}
    >
      <div className="viz-view" key={visual.id} data-mark={mark} data-visual={visual.id}>
        <VisualChart visual={raw} mark={mark} />
      </div>
    </VizCard>
  );
}

/** A figure with its unit in short form (Rp 505,3 jt, 1,4 jt). */
function leadValue(m: MetricValue): string {
  const p = figureParts(m.value, m.format);
  return p.unit ? `${p.value} ${p.unit}`.replace(/ %$/, "%") : p.value;
}

/** A stage's one or two figures, leading its first chart card. */
function LeadFigures({ metrics }: { metrics: MetricValue[] }) {
  return (
    <div className="viz-leads">
      {metrics.map((m) => (
        <div className="viz-lead" key={m.id} data-metric={m.id}>
          <VizFigure value={leadValue(m)} label={m.label} />
          <MetricStatus metric={m} />
        </div>
      ))}
    </div>
  );
}

/** Two measures of the same categories as one scatter. */
function PairCard({ chart, span, lead }: { chart: ChartPlan; span: VizSpan; lead?: MetricValue[] }) {
  const { x, y } = chart.pair!;
  const xv = displayVisual(x);
  const yv = displayVisual(y);
  if (xv.kind === "trend" || xv.kind === "funnel" || yv.kind === "trend" || yv.kind === "funnel") return null;
  const byLabel = new Map(yv.data.map((d) => [d.label, d.value]));
  const data = xv.data.map((d) => ({ label: d.label, x: d.value, y: byLabel.get(d.label) ?? 0 }));
  const category = "categoryLabel" in xv ? xv.categoryLabel : "Kategori";
  return (
    <VizCard
      span={span}
      title={`${xv.valueLabel} dan ${yv.valueLabel.toLowerCase()} per ${category.toLowerCase()}`}
      description={`${data.length} ${category.toLowerCase()}, dua ukuran dalam satu bidang`}
      figure={lead ? <LeadFigures metrics={lead} /> : undefined}
      cardId={chart.id}
    >
      <XYScatter
        label={`${xv.title} dan ${yv.title}`}
        data={data}
        xLabel={xv.valueLabel}
        yLabel={yv.valueLabel}
        xFormat={metricFormatter(xv.format)}
        yFormat={metricFormatter(yv.format)}
        categoryLabel={category}
      />
    </VizCard>
  );
}

/* ---------------------------------------------------------------------
 * Rincian: every line of prose the page owes a reader, one tab stop away.
 * ------------------------------------------------------------------- */

const USE_LABEL: Record<keyof DashboardUses, string> = {
  diagnostic: "Diagnostik",
  operational: "Operasional",
  exploratory: "Eksploratif",
};

/** Rows drawn by a ranking before the rest go to the full list. */
const LIST_LIMIT = 10;

function Details({ overview, plan }: { overview: Overview; plan: DashboardPlan }) {
  const { northStar } = overview;
  const uses = (Object.keys(USE_LABEL) as (keyof DashboardUses)[])
    .filter((key) => overview.uses[key].trim().length > 0)
    .map((key): ListRowItem => ({ id: key, title: USE_LABEL[key], description: overview.uses[key] }));
  const views = plan.charts.flatMap((c) => c.views);
  const notes = views
    .filter(({ visual }) => visual.summary.trim().length > 0)
    .map(({ visual }): ListRowItem => ({ id: visual.id, title: visual.title, description: visual.summary }));
  const long = views.filter(({ visual }) => visual.kind !== "trend" && visual.data.length > LIST_LIMIT);
  return (
    <Disclosure title="Rincian metrik dan catatan dokumen" description="Rumus, dasar penilaian, metrik kunci dokumen, catatan tiap grafik, daftar lengkap, dan pemakaian dashboard.">
      {northStar.formula.trim() || northStar.metric ? (
        <div className="viz-detail" data-detail="north-star">
          <h3 className="viz-detail-title">North Star</h3>
          {northStar.formula.trim() ? <p className="viz-detail-text">Rumus inti di dokumen: {northStar.formula}</p> : null}
          {northStar.metric && northStar.metric.trend.length >= 2 ? <p className="viz-detail-text">{`${trendLabel(northStar.metric)}.`}</p> : null}
        </div>
      ) : null}
      {overview.stages.map((stage) => {
        const metrics = stageMetrics(stage, overview.metrics);
        return (
          <div className="viz-detail" key={stage.id} data-stage={stage.id}>
            <h3 className="viz-detail-title">{stage.label}</h3>
            <p className="viz-detail-text">
              Owner: {stage.owner}.{stage.drivers.length > 0 ? ` Driver: ${stage.drivers.join(", ")}.` : ""}
            </p>
            <p className="viz-detail-text">Metrik kunci di dokumen: {stage.keyMetricsText}</p>
            {metrics.length > 0 ? <ListRows label={`Rincian metrik tahap ${stage.label}`} items={metrics.map(metricRow)} /> : null}
          </div>
        );
      })}
      {notes.length > 0 ? (
        <div className="viz-detail" data-detail="charts">
          <h3 className="viz-detail-title">Catatan tiap grafik</h3>
          <ListRows label="Catatan tiap grafik" items={notes} />
        </div>
      ) : null}
      {long.map(({ visual }) =>
        visual.kind === "trend" ? null : (
          <div className="viz-detail" key={visual.id} data-visual={visual.id}>
            <h3 className="viz-detail-title">{visual.title}</h3>
            <p className="viz-detail-text">{`Daftar lengkap, ${visual.data.length} baris. Grafiknya menampilkan ${LIST_LIMIT} teratas.`}</p>
            <ListRows
              label={`Daftar lengkap ${visual.title}`}
              items={[...visual.data]
                .sort((a, b) => b.value - a.value)
                .map((row, i) => ({ id: `${visual.id}-${i}`, title: row.label, trailing: <span className="viz-value">{formatMetric(row.value, visual.format)}</span> }))}
            />
          </div>
        ),
      )}
      {uses.length > 0 ? (
        <div className="viz-detail" data-detail="uses">
          <h3 className="viz-detail-title">Pemakaian di dashboard</h3>
          <ListRows label="Pemakaian di dashboard" items={uses} />
        </div>
      ) : null}
    </Disclosure>
  );
}
